import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { BellOff, Plus, Search, Users } from 'lucide-react'
import { fetchContent, text } from '../api/content'
import { CONVERSATIONS_KEY, fetchConversations, NotHereError, whenLabel } from '../api/conversations'
import { initials } from '../api/people'

/**
 * Every conversation this person is in, newest activity first (M3, D-617).
 *
 * **One flat list, sorted by recency.** No split between chats and channels,
 * because a dance studio has one kind of conversation - the Discord half of
 * the brief. A row is the other person or the group's title, the last line,
 * when, and bold while anything in it is unread.
 *
 * **No shell and no heading of its own.** `DestinationScreen` supplies both
 * from the canonical destination list; the first cut of this screen supplied
 * them again and a screen reader announced *Messages* twice.
 *
 * **A failed load is never an empty list** (D-209). Every database-backed
 * route refuses for ~75 seconds after a deploy, and *no conversations yet*
 * during that window would be a statement about the data that is not true.
 */
export default function MessagesView() {
  const { data: content } = useQuery({ queryKey: ['content'], queryFn: fetchContent })
  /*
   * <b>It refetches on a loop now, because it is never unmounted</b> (D-702).
   *
   * <b>This was free before the two-pane layout and is not any more.</b> Under
   * three flat routes this screen unmounted whenever a conversation was opened
   * and <i>remounted on every return</i>, so the list refetched constantly by
   * accident. Nested, it mounts once per visit to the destination and never
   * again — `staleTime` is 30s but nothing observes it, `refetchOnWindowFocus`
   * is off, and the only invalidator is `markRead` on the conversation that is
   * <i>already open</i>. So a parent could post in another thread at 09:20 and
   * that row would keep its old last line and no unread badge until the page
   * was reloaded.
   *
   * <b>A visible surface asserting something false</b>, which is D-209 one
   * screen over — and the screen is now always on show, which is the whole
   * point of the change. Found by an audit reading what the nesting took away
   * rather than what it added.
   *
   * <b>15 seconds, the same as the open transcript</b>, because it is now in
   * the same position: permanently on screen and permanently looked at. The
   * rail's own unread badge has the identical fault and D-638 already records
   * it; that is a wider change than this and is left named rather than half
   * done.
   */
  /*
   * **Debounced, because this refetches on every keystroke otherwise.** 250ms
   * is below what reads as lag and well above a typing rhythm.
   */
  const [search, setSearch] = useState('')
  const [term, setTerm] = useState('')

  useEffect(() => {
    const at = setTimeout(() => setTerm(search), 250)
    return () => clearTimeout(at)
  }, [search])

  const list = useQuery({
    // The term is part of the key, so a stale result for the previous one
    // cannot land after the new one.
    queryKey: [...CONVERSATIONS_KEY, term],
    queryFn: () => fetchConversations(term),
    refetchInterval: 15_000,
  })

  /*
   * <b>Which row is open, so the list can say so</b> (D-702). Read off the
   * path rather than from `useParams`, because this component is the layout's
   * child and not the one mounted on `/messages/:id` — it has no params of its
   * own to read.
   *
   * <b>It is a left pane now, and a list nobody can locate themselves in is
   * the thing that makes a two-pane layout feel worse than one screen.</b>
   */
  const { pathname } = useLocation()

  const now = new Date()

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        {/*
          <b>Only when the list is the whole screen</b> (D-702). In the pane it
          is a paragraph of preamble above the thing she came to use, in a
          column 18rem wide — and the destination heading beside it already
          says what this is.
        */}
        <p className="text-[13px] leading-relaxed text-ink-2 md:hidden">
          {text(content, 'messages.intro', 'Your conversations with the studio, teachers and families.')}
        </p>
        {/*
          <b>Full width once it is the only thing in the pane</b> (D-702).
          With the intro hidden beside it, a `justify-between` row leaves this
          floating against the left edge with a column of empty pane to its
          right - `observed` at 1280, and it reads as something that has come
          loose rather than as the compose button. Both apps the owner named
          anchor it across the column.
        */}
        <Link
          to="/messages/new"
          className="inline-flex min-h-[44px] shrink-0 items-center gap-2 rounded-lg bg-accent px-4 text-[13px] font-semibold text-on-accent md:w-full md:justify-center"
        >
          <Plus size={16} aria-hidden="true" />
          {text(content, 'messages.new', 'New message')}
        </Link>
      </div>

      {/*
        Search (Q5 of the messaging spec, carried unscoped until now). It
        narrows this list rather than opening a screen of its own, and reaches
        names, group titles and the words of messages she can already open.
      */}
      <div className="relative">
        <Search
          size={15}
          aria-hidden="true"
          className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-ink-3"
        />
        <input
          id="messages-search"
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={text(content, 'messages.search', 'Search messages')}
          aria-label={text(content, 'messages.search', 'Search messages')}
          className="min-h-[44px] w-full rounded-lg border border-line bg-surface pr-3 pl-9 text-[13px] text-ink placeholder:text-ink-3"
        />
      </div>

      {list.isPending && (
        <p className="text-[13px] text-ink-2" role="status">
          {text(content, 'messages.loading', 'Loading…')}
        </p>
      )}

      {list.isError && (
        <div className="rounded-2xl border border-line bg-surface px-5 py-6" role="alert">
          <p className="text-[13px] leading-relaxed text-ink-2">
            {/*
              * Its own key: a 404 on the list means the module is off on this
              * host, which is a different sentence from a conversation that is
              * not hers, and one seeded body cannot say both.
              */}
            {list.error instanceof NotHereError
              ? text(content, 'messages.list.notHere', 'Messages are not available here.')
              : text(content, 'messages.failed', 'Your messages could not be loaded. Try again in a moment.')}
          </p>
        </div>
      )}

      {list.isSuccess && list.data.length === 0 && (
        <div className="rounded-2xl border border-line bg-surface px-5 py-6">
          <p className="text-[14px] font-semibold">
            {text(content, 'messages.empty.title', 'No conversations yet')}
          </p>
          <p className="mt-1 text-[13px] leading-relaxed text-ink-2">
            {text(content, 'messages.empty', 'Start one with New message. You can reach the studio and anybody the timetable connects you with.')}
          </p>
        </div>
      )}

      {list.isSuccess && list.data.length > 0 && (
        <ul className="divide-y divide-line overflow-hidden rounded-2xl border border-line bg-surface">
          {list.data.map((row) => (
            <li key={row.id}>
              <Link
                to={`/messages/${row.id}`}
                aria-current={pathname === `/messages/${row.id}` ? 'page' : undefined}
                className={`flex min-h-[44px] items-start gap-3 px-4 py-3 hover:bg-surface-2 ${
                  pathname === `/messages/${row.id}` ? 'bg-accent-soft hover:bg-accent-soft' : ''
                }`}
              >
                {row.kind === 'Group' ? (
                  <span
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-surface-2 text-ink-2"
                    aria-hidden="true"
                  >
                    <Users size={18} />
                  </span>
                ) : (
                  <span
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent-soft text-[13px] font-bold text-accent-2"
                    aria-hidden="true"
                  >
                    {initials(row.title)}
                  </span>
                )}

                <span className="flex min-w-0 grow flex-col gap-0.5">
                  <span className="flex items-baseline gap-2">
                    <span
                      className={`grow truncate text-[14px] ${row.unread > 0 ? 'font-bold' : 'font-semibold'}`}
                    >
                      {row.title}
                    </span>
                    {row.lastAt && (
                      <span className="shrink-0 text-[11.5px] text-ink-2">{whenLabel(row.lastAt, now)}</span>
                    )}
                  </span>
                  <span
                    className={`truncate text-[12.5px] ${row.unread > 0 ? 'font-semibold text-ink' : 'text-ink-2'}`}
                  >
                    {row.match ?? row.lastLine ?? text(content, 'messages.nothingYet', 'Nothing said yet')}
                  </span>
                </span>

                <span className="flex shrink-0 items-center gap-2 pt-1">
                  {row.isMuted && (
                    <BellOff
                      size={14}
                      className="text-ink-2"
                      aria-label={text(content, 'messages.muted', 'Muted')}
                    />
                  )}
                  {row.unread > 0 && (
                    <span
                      className="rounded-full bg-accent px-2 py-0.5 text-[11px] font-semibold text-on-accent"
                      aria-label={`${row.unread} unread`}
                    >
                      {row.unread}
                    </span>
                  )}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
